import React from 'react';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaClock, FaAngleRight } from 'react-icons/fa';
import './activity-card.css';

/**
 * One card for every activity listing on the site: lesson plans, grammar,
 * reading, vocabulary, speaking and videos all render this, so they look
 * identical apart from their content.
 *
 * It grew out of the lesson-plans (topic) card, which was the most complete of
 * the four styles it replaces: themed tokens, a 16/10 media block with a muted
 * fill, grey meta pills, a category ribbon, and a focus-within ring.
 *
 *   to          link target. Omit it for a card that is not a link (see
 *               extraLinks) -- nesting links inside a link is invalid HTML, so
 *               a card with several targets stays a container and lists them.
 *   title       required
 *   subtitle    one short line under the title, in the accent colour
 *   description clamped to three lines
 *   image       backdrop; omitted cards still reserve the media block height so
 *               a row of cards lines up
 *   category    ribbon label over the image, uppercased here
 *   accent      any CSS colour; defaults to the primary. Set per category by
 *               categoryAccent() so it follows the theme.
 *   level       CEFR code, rendered as a colour-coded pill
 *   time        short duration/time string, rendered as a plain pill
 *   meta        optional string shown after the pills
 *   cta         label for the footer action; omit to hide it
 *   extraLinks  [{ to, text, level }] for cards that lead to several exercises.
 *               Rendered under the card rather than inside it.
 *   eager       load the image eagerly (first row of a grid)
 */
const ActivityCard = ({
  to,
  title,
  languagepoint,
  description,
  image,
  category,
  accent,
  level,
  time,
  meta,
  cta,
  extraLinks = [],
  eager = false,
}) => {
  const levelKey = level ? String(level).toLowerCase() : '';

  const body = (
    <article className="activity-card" style={accent ? { '--accent': accent } : undefined}>
      <div className="activity-card__media">
        {image && (
          <img
            src={image}
            alt=""
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            className="activity-card__image"
          />
        )}
        {category && <span className="activity-card__category">{category}</span>}
      </div>

      <div className="activity-card__content">
        {(level || time || languagepoint || meta) && (
          <div className="activity-card__meta">
            {level && (
              <span className={`activity-card__pill activity-card__pill--level is-level-${levelKey}`}>
                <FaGraduationCap className="activity-card__pill-icon" aria-hidden="true" />
                {level}
              </span>
            )}
            {time && (
              <span className="activity-card__pill">
                <FaClock className="activity-card__pill-icon" aria-hidden="true" />
                {time}
              </span>
            )}
            {languagepoint && (
              <span className="activity-card__pill activity-card__pill--focus">
                {languagepoint}
              </span>
            )}
            {meta && <span className="activity-card__meta-text">{meta}</span>}
          </div>
        )}

        <h2 className="activity-card__title">{title}</h2>
        {description && <p className="activity-card__description">{description}</p>}

        {cta && <span className="activity-card__cta">{cta}</span>}
      </div>
    </article>
  );

  return (
    <div className="activity-card__item">
      {to ? (
        <Link to={to} className="activity-card__link">
          {body}
        </Link>
      ) : (
        body
      )}

      {extraLinks.length > 0 && (
        <ul className="activity-card__links">
          {extraLinks.map((link, i) => (
            <li key={i}>
              <Link to={link.to} className="activity-link-item">
                <span>
                  {link.level && (
                    <span className={`label label-${String(link.level).toLowerCase()}`}>
                      {link.level}
                    </span>
                  )}
                  {link.text}
                </span>
                <FaAngleRight aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActivityCard;